import { useContext, useEffect, useState } from "react";
import { FeatureRenderer } from "src/components/FeatureRenderer";
import { GlassInputControl, GlassInputGroup, GlassTextInputTransformer } from "src/components/GlassInput";
import { addElement, FRPAction, FRPActionType, FRPContext, FRPElement, FRPElementType, FRPNotificationContext, setSelectedElementIds, deleteElements, setShownElementsIds, setTag, viewedElement, updateProject } from "./FRPState";
import { feature, featureCollection } from "@turf/helpers";
import { Localization, SessionContext } from "@viamap/viamap2-common";
import { Typeahead } from "react-bootstrap-typeahead";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { ViewButtonIcon } from "src/propertyInfoTemplates/PropertyComponents";
import { FRPFunc } from "./FRPFunc";
import { RandomColorHex } from "src/components/ComponentUtils";
import { ProtectedFeature } from "src/components/ProtectedFeature";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { openWindow, WindowId } from "src/WindowManager/WindowState";
import { FRPElementStatus } from "./FRPProjektView";
import { MapitUtils } from "src/managers/MapitUtils";
import { MIBIcon } from "src/componentsUtils/MIBIcon";

export function FRPElementEditor(props: {
   hoverElement: (elementID: string | string[] | null) => void,
   exitElement: () => void
}) {

   let { state: frpState, dispatch: frpDispatch } = useContext(FRPContext);
   let { state: sessionState } = useContext(SessionContext);
   const { dispatch: dispatchNotification } = useContext(FRPNotificationContext)
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const { dispatch: windowDispatch } = useWindowContext();


   let element = handleMultipleElements(frpState.selectedElementIds?.map((id) => frpState.elements[id]).filter((a) => a) ?? []);
   const [localDescription, setLocalDescription] = useState<string>(element?.["description"] || "");
   const [localName, setLocalName] = useState<string>(element?.name || element?.id || "");

   // Helper function to get display name (name || id)
   const getElementDisplayName = (elementId: string): string => {
      const el = frpState.elements[elementId];
      return el?.name || el?.id || elementId;
   };


   useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
         if (event.key === 'Escape') {
            FRPFunc.goToElement(frpState, frpDispatch, FRPFunc.getParentElements(frpState.selectedElementIds ?? [], frpState), true);
         }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
         document.removeEventListener('keydown', handleKeyDown);
      };
   }, [element]);

   useEffect(() => {
      if (element) {
         setLocalDescription(element["description"] || "");
         setLocalName(element.name || element.id || "");
         return () => {
            frpState.selectedElementIds!.forEach((id) => {
               dispatchNotification(viewedElement(id));
            });
         }
      }
   }, [frpState.selectedElementIds!]);

   function handleChanges(key, value) {
      if (!element || !frpState.selectedElementIds!)
         return

      // Capture old values before making changes
      const oldValues = frpState.selectedElementIds!.map(id => {
         const element = frpState.elements[id];
         return {
            id,
            oldValue: element[key],
            oldDisplayValue: key === "status"
               ? frpState.statusOptions.find((a) => a.id === element[key])?.val || element[key]
               : key === "tags"
                  ? element[key]?.map(tagId => frpState.tagOptions.find((a) => a.id === tagId)?.val).filter(val => val !== undefined) || []
                  : element[key]
         };
      });

      frpState.selectedElementIds!.forEach((id) => {
         frpDispatch(addElement(
            id, {
            ...frpState.elements[id],
            lastChangedUser: sessionState.userRef,
            lastChangedTimestamp: new Date(),
            [key]: value
         }));
      });

      // Prepare display values for the message
      let displayValue = value;
      if (key === "status") {
         displayValue = frpState.statusOptions.find((a) => a.id === value)?.val || value;
      } else if (key === "tags") {
         displayValue = value.map(id => frpState.tagOptions.find((a) => a.id === id)?.val).filter(val => val !== undefined);
      }

      // Create descriptive message based on the change type
      let message = "";
      if (frpState.selectedElementIds!.length === 1) {
         const oldData = oldValues[0];
         if (key === "status") {
            message = `Changed status from "${oldData.oldDisplayValue}" to "${displayValue}"`;
         } else if (key === "tags") {
            const oldTags = oldData.oldDisplayValue || [];
            const newTags = displayValue || [];
            const addedTags = newTags.filter(tag => !oldTags.includes(tag));
            const removedTags = oldTags.filter(tag => !newTags.includes(tag));

            if (addedTags.length > 0 && removedTags.length > 0) {
               message = `Added tags: ${addedTags.join(", ")} and removed tags: ${removedTags.join(", ")}`;
            } else if (addedTags.length > 0) {
               message = `Added tags: ${addedTags.join(", ")}`;
            } else if (removedTags.length > 0) {
               message = `Removed tags: ${removedTags.join(", ")}`;
            } else {
               message = `Updated tags to: ${newTags.join(", ")}`;
            }
         } else {
            message = `Changed ${key} from "${oldData.oldDisplayValue}" to "${displayValue}"`;
         }

         let actionObj: FRPAction = {
            type: FRPElementType.Action,
            id: MapitUtils.getNewUuid(),
            user: sessionState.userRef,
            date: new Date(),
            elementId: frpState.selectedElementIds![0],
            action: FRPActionType.Update,
            message: message
         }
         frpDispatch(addElement(actionObj.id, actionObj));
         return;
      } else {
         // For multiple elements, create a summary message
         const uniqueOldValues = [...new Set(oldValues.map(v => v.oldDisplayValue))];
         const uniqueOldValuesStr = uniqueOldValues.length === 1 ? uniqueOldValues[0] : `${uniqueOldValues.length} different values`;

         if (key === "status") {
            message = `Changed status from ${uniqueOldValuesStr} to "${displayValue}" for ${frpState.selectedElementIds!.length} elements`;
         } else if (key === "tags") {
            message = `Updated tags to "${displayValue.join(", ")}" for ${frpState.selectedElementIds!.length} elements`;
         } else {
            message = `Changed ${key} from ${uniqueOldValuesStr} to "${displayValue}" for ${frpState.selectedElementIds!.length} elements`;
         }

         let actionObj: FRPAction = {
            type: FRPElementType.Action,
            id: MapitUtils.getNewUuid(),
            user: sessionState.userRef,
            date: new Date(),
            elementId: undefined,
            action: FRPActionType.Update,
            message: message
         }
         frpDispatch(addElement(actionObj.id, actionObj));
      }
   }





   let expectedStartDate = FRPFunc.determineParentAttribute(element, frpState, "expectedStartDate");
   let expectedEndDate = FRPFunc.determineParentAttribute(element, frpState, "expectedEndDate");

   // Get status from children (worst status) - always inferred from children
   const statusFromChildren = FRPFunc.determineNestedChildrenStatusWithSource(element, frpState);
   const hasChildren = frpState.childrenLookupTable[element.id]?.length > 0;
   const childSourceIds = statusFromChildren.elementIds || [];

   // If element has children, status is always inferred from children
   const status = hasChildren
      ? { value: statusFromChildren.status || "", elementId: childSourceIds[0], sourceIds: childSourceIds }
      : (element.status ? { value: element.status, elementId: element.id, sourceIds: [element.id] } : undefined);
   let hierarchy = FRPFunc.getElementHierarchy(element.id, frpState);
   hierarchy.unshift(frpState.selectedProject?.name ?? "Project?");


   // MARK: - RETURN
   return (
      <div className="FRP_featureCon">
         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
            {frpState.selectedElementIds!.length === 1 ? (
               <input
                  type="text"
                  value={localName}
                  onChange={(e) => setLocalName(e.target.value)}
                  onBlur={() => {
                     const trimmedName = localName.trim();
                     const currentName = element?.name || element?.id || "";
                     // If cleared, set to undefined (will default to id in display)
                     const newName = trimmedName === "" ? undefined : (trimmedName === element?.id ? undefined : trimmedName);
                     if (newName !== element?.name) {
                        handleChanges("name", newName);
                     } else if (trimmedName !== currentName) {
                        // Reset to current value
                        setLocalName(currentName);
                     }
                  }}
                  onKeyDown={(e) => {
                     if (e.key === 'Enter') {
                        e.currentTarget.blur();
                     }
                  }}
                  disabled={!hasAccessToFeature(Feature.FRPProjectWrite)}
                  className="FRP_featureTitle"
                  style={{
                     flex: 1,
                     minWidth: 0,
                     margin: 0,
                     overflow: 'hidden',
                     textOverflow: 'ellipsis',
                     whiteSpace: 'nowrap',
                     fontWeight: 'bold',
                     fontSize: '1.4em',
                     marginLeft: "3px",
                     border: 'none',
                     background: 'transparent',
                     padding: '2px 4px',
                     cursor: hasAccessToFeature(Feature.FRPProjectWrite) ? 'text' : 'default'
                  }}
               />
            ) : (
               <h5 className="FRP_featureTitle" style={{ flex: 1, minWidth: 0, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 'bold', fontSize: '1.4em', marginLeft: "3px" }}>
                  {Localization.getFormattedText("{count} elements selected", { count: frpState.selectedElementIds!.length })}
               </h5>
            )}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginRight: "4px", flexShrink: 0 }}>
               <ProtectedFeature feature={Feature.FRPProjectWrite} contentsIfNoAccess={<></>}>

                  <ViewButtonIcon
                     title={Localization.getText("Delete")}
                     onClick={() => {
                        windowDispatch(openWindow(WindowId.ConfirmationDialog, {
                           header: "Confirm deletion",
                           children: (frpState.selectedElementIds?.length || 0) > 1 ? Localization.getFormattedText("Confirm delete of {amount} elements?", { amount: frpState.selectedElementIds!.length }) : Localization.getFormattedText("Confirm delete of element {elementId}?", { elementId: getElementDisplayName(element.id) }),
                           onSubmit: () => {
                              FRPFunc.goToElement(frpState, frpDispatch, frpState.elements[frpState.shownElementsIds?.[0]]?.refElementId); // TODO: Deselect elements before dispatching delete to avoid issues, instead of going back
                              frpDispatch(deleteElements(frpState.selectedElementIds ?? []));
                           }
                        }))
                     }}
                  >

                     <MIBIcon icon="Remove" />
                  </ViewButtonIcon>
               </ProtectedFeature>
               <ViewButtonIcon
                  title={element.refElementId == undefined || element.refElementId === "_various" ? Localization.getText("Go back w keyword") : Localization.getText("Go back w keyword")}

                  onClick={() => {
                     FRPFunc.goToElement(frpState, frpDispatch, frpState.elements[frpState.shownElementsIds?.[0]]?.refElementId, true);
                  }}>
                  <MIBIcon icon="GoBack" />
               </ViewButtonIcon>
               {frpState.selectedElementIds && frpState.selectedElementIds.length === 1 && (
                  <ViewButtonIcon
                     title={Localization.getText("gå frem w keyword")}

                     onClick={() => {
                        frpDispatch(setSelectedElementIds([element.id]));
                        frpDispatch(setShownElementsIds(frpState.childrenLookupTable[element.id] ?? [element.id]));

                     }
                     }>
                     <MIBIcon icon="GoForward" />
                  </ViewButtonIcon>
               )}
            </div>
         </div>
         {hierarchy.length > 1 && frpState.selectedElementIds!.length === 1 && (
            <div className="element-hierarchy" style={{ display: "flex", alignItems: "center", gap: "5px", margin: "4px 0px 20px" }}>
               {hierarchy.map((id, index) => {
                  const isProjectName = typeof id === 'string' && index === 0;
                  const displayText = isProjectName ? id : getElementDisplayName(id);

                  return <span key={id} style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                     {/* Arrow completely outside any styled spans */}
                     {index === 0 ? "" : <MIBIcon icon="GoForward" />}

                     <span className="FRP_goToElementer"
                        onClick={() => {
                           FRPFunc.goToElement(frpState, frpDispatch, isProjectName ? undefined : id);
                        }}
                        style={{
                           cursor: "pointer",
                           maxWidth: "120px",
                           overflow: "hidden",
                           textOverflow: "ellipsis",
                           whiteSpace: "nowrap"
                        }}
                        title={displayText}
                     >
                        {displayText}
                     </span>
                  </span>
               })}
            </div>
         )}
         {/* MARK: - STATUS BAR */}
         {hasChildren && (
            <div style={{ margin: "0px 0px 10px 6px" }}>
               <FRPElementStatus
                  elements={
                     (
                        frpState.selectedElementIds!
                           .flatMap(selId => frpState.childrenLookupTable[selId] ?? [])
                           .map(id => frpState.elements[id])
                     )
                  }
                  onHover={(ids) => props.hoverElement(ids)}
                  setIsEyeActive={() => { }}
               />
            </div>
         )}
         <GlassInputGroup style={{ position: "relative" }}>
            <div id="FRP_FRCon">
               <FeatureRenderer
                  feature={function () {
                     let features = frpState.selectedElementIds!.map((id) => frpState.elements?.[id]?.geometry).filter((a) => a);
                     if (features.length > 0) {
                        return featureCollection(frpState.selectedElementIds!.map((id) => feature(frpState.elements?.[id]?.geometry)));
                     } else {
                        return featureCollection([]);
                     }
                  }()}
                  id="FRP_FRCon"
                  width={1}
                  color="#334b49"
               />
            </div>
            <GlassTextInputTransformer label={Localization.getText("Status")} >
               <GlassInputControl>
                  {hasChildren && (
                     <ViewButtonIcon
                        title={(() => {
                           if (!status?.elementId) {
                              return Localization.getText("Status inferred from children");
                           }
                           const sourceName = getElementDisplayName(status.elementId);
                           const suffix = status.sourceIds && status.sourceIds.length > 1 ? " " + Localization.getText("And others abbreviation") : "";
                           const nameWithSuffix = sourceName + suffix;
                           return status.value
                              ? Localization.getFormattedText("Inherited from: {name}", { name: nameWithSuffix })
                              : Localization.getFormattedText("No status from: {name}", { name: nameWithSuffix });
                        })()}
                        className="no-hover-bg"
                        style={{ backgroundColor: 'transparent' }}
                        onClick={() => {
                           if (status?.elementId) {
                              FRPFunc.goToElement(frpState, frpDispatch, status.elementId);
                           }
                        }}
                        onMouseEnter={() => {
                           if (status?.elementId) {
                              props.hoverElement?.(status.elementId);
                           }
                        }}
                        onMouseLeave={() => {
                           props.exitElement?.();
                        }}>
                        <MIBIcon icon="Global" style={{ color: "black" }} />
                     </ViewButtonIcon>
                  )}
                  <select
                     disabled={!hasAccessToFeature(Feature.FRPProjectWrite)}
                     className="GlassInputSelect"
                     value={status?.value || ""}
                     style={{
                        color: (!status || status.value === "") ? "#888" : "black"
                     }}
                     onChange={(e) => {
                        handleChanges("status", e.target.value);
                     }}
                     title={hasChildren && !element.status ? Localization.getText("Status is inferred from children. Setting a status will override all children's statuses.") : ""}
                  >
                     {element.status === "_various" && <option value="_various">{Localization.getText("Various values")}</option>}
                     <option value={""} style={{ color: "#888" }}>{Localization.getText("Not specified")}</option>
                     {frpState.statusOptions.map((a) => <option style={{ color: "black" }} key={a.id} value={a.id}>{a.val}</option>)}
                  </select>
               </GlassInputControl>
            </GlassTextInputTransformer>
            <GlassTextInputTransformer label={Localization.getText("Forventet startet")}>
               <GlassInputControl>
                  {expectedStartDate && expectedStartDate?.elementId !== element.id && (
                     <ViewButtonIcon
                        title={expectedStartDate?.elementId ? Localization.getFormattedText("Inherited from: {name}", { name: getElementDisplayName(expectedStartDate.elementId) }) : ""}
                        className="no-hover-bg"
                        style={{ backgroundColor: 'transparent' }}
                        onClick={() => {
                           if (expectedStartDate?.elementId) {
                              FRPFunc.goToElement(frpState, frpDispatch, expectedStartDate.elementId);
                           }
                        }}
                        onMouseEnter={() => {
                           if (expectedStartDate?.elementId) {
                              props.hoverElement?.(expectedStartDate.elementId);
                           }
                        }}
                        onMouseLeave={() => {
                           props.exitElement?.();
                        }}>
                        <MIBIcon icon="Global" style={{ color: "black" }} />
                     </ViewButtonIcon>
                  )}
                  <input
                     type="date"
                     disabled={!hasAccessToFeature(Feature.FRPProjectWrite)}
                     value={expectedStartDate?.value ?? ""}
                     onClick={(e) => {
                        if (expectedStartDate && expectedStartDate?.elementId !== element.id) {
                           (e.target as HTMLInputElement).value = "";
                        }
                     }}
                     onChange={(e) => {
                        handleChanges("expectedStartDate", (e.target as HTMLInputElement).value || undefined);
                     }}
                  />

               </GlassInputControl>
            </GlassTextInputTransformer>
            <GlassTextInputTransformer label={Localization.getText("Forventet færdig")}>
               <GlassInputControl>
                  {expectedEndDate && expectedEndDate?.elementId !== element.id && (
                     <ViewButtonIcon
                        title={expectedEndDate?.elementId ? Localization.getFormattedText("Inherited from: {name}", { name: getElementDisplayName(expectedEndDate.elementId) }) : ""}
                        className="no-hover-bg"
                        style={{ backgroundColor: 'transparent' }}
                        onClick={() => {
                           if (expectedEndDate?.elementId) {
                              FRPFunc.goToElement(frpState, frpDispatch, expectedEndDate.elementId);
                           }
                        }}
                        onMouseEnter={() => {
                           if (expectedEndDate?.elementId) {
                              props.hoverElement?.(expectedEndDate.elementId);
                           }
                        }}
                        onMouseLeave={() => {
                           props.exitElement?.();
                        }}>
                        <MIBIcon icon="Global" style={{ color: "black" }} />
                     </ViewButtonIcon>
                  )}
                  <input
                     type="date"
                     disabled={!hasAccessToFeature(Feature.FRPProjectWrite)}
                     value={expectedEndDate?.value ?? ""}
                     onClick={(e) => {
                        if (expectedStartDate && expectedStartDate?.elementId !== element.id) {
                           (e.target as HTMLInputElement).value = "";
                        }
                     }}
                     onChange={(e) => {
                        handleChanges("expectedEndDate", e.target.value || undefined);
                     }}
                  />
               </GlassInputControl>
            </GlassTextInputTransformer>

            <GlassTextInputTransformer label={Localization.getText("Refereret element")}>
               <Typeahead
                  className="EmneInfo"
                  id={"mytypeaheadRefElement"}
                  multiple={true}
                  options={function () {
                     if (!element?.id || !frpState.elements[element.id]) return [];
                     let nestedChildren = FRPFunc.allNestedChildrenIds(frpState.elements[element.id].id, frpState.childrenLookupTable);
                     return Object.values(frpState.elements)
                        .filter((a) => a.id !== element.id && !nestedChildren.includes(a.id))
                        .map((a) => {
                           const displayName = getElementDisplayName(a.id);
                           return {
                              label: displayName,
                              nlabel: `${displayName} ${a.id}`, // Search by both name and id
                              id: a.id // Store the actual id for the value
                           };
                        });
                  }()}
                  allowNew={false}
                  onChange={(selected: any) => {
                     const selectedItem = selected.at(-1);
                     handleChanges("refElementId", selectedItem?.id || selectedItem?.label || "");
                  }}
                  selected={element.refElementId ? [{
                     label: getElementDisplayName(element.refElementId),
                     nlabel: getElementDisplayName(element.refElementId),
                     id: element.refElementId
                  }] : []}
                  disabled={!hasAccessToFeature(Feature.FRPProjectWrite)}
               />
            </GlassTextInputTransformer>
            <GlassTextInputTransformer label={Localization.getText("Tags")} >
               {element.tags == "_various" ?
                  <span>{Localization.getText("Various values")}</span> :
                  <Typeahead
                     className="EmneInfo"
                     id={"mytypeahead"}
                     multiple={true}
                     options={
                        frpState.tagOptions.map((a) => { return { label: a.val, id: a.id, color: a.color } })
                     }
                     allowNew={hasAccessToFeature(Feature.FRPProjectAdmin)}
                     labelKey={"label"}
                     clearButton={false}
                     style={{ maxWidth: "100%" }}
                     minLength={0}
                     placeholder={Localization.getText("Start typing to select tags")}

                     newSelectionPrefix={Localization.getText("Add new tag") + " "}
                     emptyLabel={"No matches found"}
                     onChange={(selected: any) => {
                        let customNew = selected.filter((a) => a.customOption) as { id: string, label: string }[];
                        if (customNew.length > 0) {
                           let nextTags =
                              [...frpState.tagOptions, ...(customNew).map((a) => ({
                                 id: MapitUtils.getNewUuid(),
                                 val: a.label,
                                 color: RandomColorHex()
                              }))];
                           frpDispatch(setTag(nextTags));
                           let loadedProject = frpState.selectedProject;
                           if (loadedProject) {
                              loadedProject.settings.tagOptions = nextTags;
                              frpDispatch(updateProject(loadedProject.id, loadedProject));
                           }
                           const updatedSelected = selected.map((item: any) => {
                              if (item.customOption) {
                                 const newTag = nextTags.find(tag => tag.val === item.label);
                                 return { ...item, id: newTag?.id || item.id };
                              }
                              return item;
                           });
                           handleChanges("tags", updatedSelected.map((a: any) => a.id));
                        } else {
                           handleChanges("tags", selected.map((a) => a.id));
                        }
                     }}
                     disabled={!hasAccessToFeature(Feature.FRPProjectWrite)}
                     selected={element.tags ? element.tags.map((a) => {
                        let currentTag = frpState.tagOptions.find((t) => t.id === a)
                        return currentTag ? { label: currentTag.val, id: currentTag.id, color: currentTag.color } : undefined;
                     }).filter((a) => a) as any : []}
                     renderToken={(option: any, rTprops: any) => {
                        const tagColor = option.color || "#808080";
                        const hexToRgba = (hex: string, alpha: number) => {
                           const r = parseInt(hex.slice(1, 3), 16);
                           const g = parseInt(hex.slice(3, 5), 16);
                           const b = parseInt(hex.slice(5, 7), 16);
                           return `rgba(${r}, ${g}, ${b}, ${alpha})`;
                        };
                        const backgroundColor = tagColor.startsWith('#') ? hexToRgba(tagColor, 0.3) : tagColor.replace('1)', '0.3)');
                        return (
                           <div
                              className="rbt-token rbt-token-removable"
                              style={{
                                 backgroundColor: backgroundColor,
                                 borderLeft: `4px solid ${tagColor}`,
                                 color: "#000"
                              }}
                           >
                              <div className="rbt-token-label">{option.label}</div>
                              {hasAccessToFeature(Feature.FRPProjectWrite) && (
                                 <button
                                    className="close btn-close rbt-close rbt-token-remove-button"
                                    onClick={() => rTprops.onRemove?.(option)}
                                 >
                                    ×
                                 </button>
                              )}
                           </div>
                        );
                     }}
                  />}
            </GlassTextInputTransformer>
            <GlassTextInputTransformer label={Localization.getText("Beskrivelse")} >
               <textarea className="GlassInputTextArea" value={localDescription}
                  disabled={!hasAccessToFeature(Feature.FRPProjectWrite)}
                  onChange={(e) => {
                     setLocalDescription(e.currentTarget.value);
                  }}
                  onBlur={() => {
                     if (localDescription !== element["description"]) {
                        handleChanges("description", localDescription);
                     }
                  }} />
            </GlassTextInputTransformer>

         </GlassInputGroup>
      </div>
   );
}



const keysViewedInEditor = ["status", "expectedStartDate", "expectedEndDate", "refElementId", "tags", "description"] as const;
type FRPMultiElement = FRPElement & {
   [key in typeof keysViewedInEditor[number]]?: FRPElement[key] | "_various"
};

function handleMultipleElements(elements: FRPElement[]): FRPMultiElement {
   let x = Object.fromEntries(keysViewedInEditor.map((key) => {
      return [key, returnVariousIfMultipleOptions(key, elements)];
   }));

   return {
      ...elements[0],
      ...x
   };
}

function returnVariousIfMultipleOptions(key, elements: FRPElement[]): any {
   const values = new Set(elements.map((el) => el?.[key] ?? ""))
   return values.size > 1 ? "_various" : [...values][0];
}